package com.mps.asc_app.presentation

import com.mps.asc_app.data.functions.FunctionsRepository
import com.mps.asc_app.data.user.MemberSummary
import com.mps.asc_app.data.user.UserRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout

data class AdminPanelUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val admins: List<MemberSummary> = emptyList(),
    val trainers: List<MemberSummary> = emptyList(),
    val approved: List<MemberSummary> = emptyList(),
    val pending: List<MemberSummary> = emptyList(),
    val emailNotVerified: List<MemberSummary> = emptyList(),
    val resendLoadingUids: Set<String> = emptySet(),
    val resendSuccessUids: Set<String> = emptySet(),
    val resendErrorByUid: Map<String, String> = emptyMap(),
    val testUsersResetLoading: Boolean = false,
    val testUsersResetFeedback: String? = null,
)

internal const val MEMBER_LOAD_ATTEMPTS = 2
internal const val MEMBER_LOAD_TIMEOUT_MILLIS = 30_000L

class AdminPanelViewModel(
    private val userRepository: UserRepository,
    private val functionsRepository: FunctionsRepository,
) : BaseViewModel() {

    private val _state = MutableStateFlow(AdminPanelUiState())
    val state: StateFlow<AdminPanelUiState> = _state

    init {
        refresh()
    }

    fun refresh() {
        scope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            // Der erste Firestore-Abruf im Browser braucht manchmal lange (Verbindungsaufbau):
            // großzügiges Zeitlimit und ein zweiter Versuch, bevor ein Fehler angezeigt wird.
            runCatching {
                loadMembersWithRetry()
            }
                .onSuccess { members ->
                    val sorted = members
                        .filter { member ->
                            // "Unbekannte" Mitglieder ohne Name und E-Mail ausblenden
                            !(member.displayName == "Unbekannt" && member.email.isBlank())
                        }
                        .sortedBy { it.displayName.lowercase() }

                    val admins = sorted.filter { member -> member.isAdmin }
                    val nonAdminMembers = sorted.filter { member -> !member.isAdmin }

                    val trainers = nonAdminMembers.filter { member ->
                        member.isTrainer
                    }

                    val approvedMembers = nonAdminMembers.filter { member ->
                        !member.isTrainer
                    }
                    val openMembers = approvedMembers

                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            admins = admins,
                            trainers = trainers,
                            approved = approvedMembers,
                            pending = emptyList(),
                            emailNotVerified = emptyList(),
                            resendLoadingUids = emptySet(),
                        )
                    }

                    // Danach: E-Mail-Verifizierungsflags nachladen (damit Gruppen korrekt sind)
                    scope.launch {
                        val flagsResult = runCatching {
                            withTimeout(15_000) {
                                functionsRepository.getEmailVerifiedFlags(openMembers.map { it.uid })
                            }
                        }.getOrElse { Result.failure(it) }

                        if (flagsResult.isSuccess) {
                            val emailVerifiedFlags = flagsResult.getOrThrow()
                            val emailNotVerifiedMembers = openMembers.filter { member ->
                                emailVerifiedFlags[member.uid] != true
                            }

                            _state.update { state ->
                                state.copy(
                                    pending = emptyList(),
                                    emailNotVerified = emailNotVerifiedMembers
                                )
                            }
                        }
                    }
                }
                .onFailure { e ->
                    val msg = when (e) {
                        is TimeoutCancellationException -> "Timeout beim Laden der Mitglieder. Bitte erneut versuchen."
                        else -> e.message ?: "Mitglieder konnten nicht geladen werden."
                    }
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = msg
                        )
                    }
                }
        }
    }

    private suspend fun loadMembersWithRetry(): List<MemberSummary> {
        var lastError: Throwable? = null
        repeat(MEMBER_LOAD_ATTEMPTS) {
            try {
                return withTimeout(MEMBER_LOAD_TIMEOUT_MILLIS) { userRepository.getAllMembers() }
            } catch (e: TimeoutCancellationException) {
                lastError = e
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                lastError = e
            }
        }
        throw lastError ?: IllegalStateException("Mitglieder konnten nicht geladen werden.")
    }

    fun resendVerificationEmailForUser(uid: String) {
        val cleanedUid = uid.trim()
        if (cleanedUid.isBlank()) return

        scope.launch {
            _state.update { state ->
                state.copy(
                    resendLoadingUids = state.resendLoadingUids + cleanedUid,
                    resendSuccessUids = state.resendSuccessUids - cleanedUid,
                    resendErrorByUid = state.resendErrorByUid - cleanedUid
                )
            }

            val result = functionsRepository.resendEmailVerification(cleanedUid)

            _state.update { state ->
                result.fold(
                    onSuccess = {
                        state.copy(
                            resendLoadingUids = state.resendLoadingUids - cleanedUid,
                            resendSuccessUids = state.resendSuccessUids + cleanedUid,
                            resendErrorByUid = state.resendErrorByUid - cleanedUid
                        )
                    },
                    onFailure = { e ->
                        state.copy(
                            resendLoadingUids = state.resendLoadingUids - cleanedUid,
                            resendSuccessUids = state.resendSuccessUids - cleanedUid,
                            resendErrorByUid = state.resendErrorByUid + (
                                cleanedUid to (e.message ?: "E-Mail konnte nicht gesendet werden.")
                            )
                        )
                    }
                )
            }
        }
    }

    // Legt die beiden Debug-Test-Konten an bzw. setzt sie auf ihren Ausgangszustand zurueck.
    // Die Cloud Functions erlauben das nur eingeloggten Admins.
    fun resetDebugTestUsers() {
        if (_state.value.testUsersResetLoading) return

        scope.launch {
            _state.update { it.copy(testUsersResetLoading = true, testUsersResetFeedback = null) }

            val result = functionsRepository.ensureDebugTestUserExists()
                .mapCatching { functionsRepository.ensureDebugTestUserHasMemberNumberExists().getOrThrow() }

            _state.update { state ->
                state.copy(
                    testUsersResetLoading = false,
                    testUsersResetFeedback = result.fold(
                        onSuccess = { "Test-Konten wurden zurückgesetzt." },
                        onFailure = { e -> "Zurücksetzen fehlgeschlagen: ${e.message ?: "unbekannter Fehler"}" }
                    )
                )
            }
        }
    }
}
